import { isNotDefined } from '@sgme/fp';
import { useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import LiveTcaLinkButton from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/LiveTcaLinkButton.tsx';
import { userContext } from '@/context/UserContext.tsx';
import {
  useFillNowAlgoMutation,
  usePauseAlgoMutation,
  useResumeAlgoMutation,
} from '@/store/orders/algoOrders/api/index.ts';
import type { AlgoOrderType } from '@/store/orders/state.ts';

type AlgoButtonsProps = {
  isPaused: boolean;
  tradeId?: string;
  canEditOrder: boolean;
  algoType: AlgoOrderType;
};

const AlgoActionButtons = ({ isPaused, tradeId, canEditOrder, algoType }: AlgoButtonsProps) => {
  const track = useTrackEvent();
  const [pauseAlgoCall, { isLoading: isLoadingPauseCall }] = usePauseAlgoMutation();
  const [resumeAlgoCall, { isLoading: isLoadingResumeCall }] = useResumeAlgoMutation();
  const [fillNowAlgoCall, { isLoading: isLoadingFillNowCall }] = useFillNowAlgoMutation();

  const user = useContext(userContext);
  const hasFxTcaAccess = user?.currentUser.hasFxTcaAccess ?? false;

  const handlePauseResume = () => {
    track('Order action', isPaused ? 'Resume Order' : 'Pause Order');
    if (isNotDefined(tradeId)) {
      return;
    }

    isPaused
      ? resumeAlgoCall({ orderRef: tradeId, orderType: algoType })
      : pauseAlgoCall({ orderRef: tradeId, orderType: algoType });
  };

  const handleFillNow = () => {
    track('Order action', 'Fill Order');
    if (isNotDefined(tradeId)) {
      return;
    }

    fillNowAlgoCall({ orderRef: tradeId, orderType: algoType });
  };

  return (
    <div className="d-flex align-items-center justify-content-between">
      {canEditOrder && (
        <>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary btn-icon-text me-2"
            onClick={handlePauseResume}
            disabled={isLoadingPauseCall || isLoadingResumeCall}
          >
            {isLoadingPauseCall || isLoadingResumeCall ? (
              <Spinner
                style={{
                  width: '2em',
                  height: '2em',
                }}
                animation="border"
                size="sm"
              />
            ) : (
              <em className="icon">{getIcon(isPaused)}</em>
            )}

            <FormattedMessage id={!isPaused ? 'order.form.pause' : 'order.form.resume'} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary btn-icon-text me-2"
            onClick={handleFillNow}
            disabled={isLoadingFillNowCall}
          >
            {isLoadingFillNowCall ? (
              <Spinner
                style={{
                  width: '2em',
                  height: '2em',
                }}
                animation="border"
                size="sm"
              />
            ) : (
              <em className="icon">fast_forward</em>
            )}
            <FormattedMessage id="order.form.fillNow" />
          </button>
        </>
      )}
      <LiveTcaLinkButton tradeId={tradeId ?? ''} hasFxTcaAccess={hasFxTcaAccess} />
    </div>
  );
};

export default AlgoActionButtons;

function getIcon(isPaused: boolean) {
  return isPaused ? 'play_arrow' : 'pause';
}
